/**
 * DemoUpgradeCTA — sleek conversion card shown ONLY to demo users.
 *
 * Sits inside the demo dashboard between the welcome row and the live
 * activity feed. Hidden for every real customer.
 *
 * No modal. No overlay. No interrupt. Just a premium card that nudges.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isDemoUser } from '../lib/demo';

export default function DemoUpgradeCTA() {
  const { user } = useAuth();
  if (!isDemoUser(user)) return null;

  return (
    <section
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 via-rose-500 to-amber-500 text-white shadow-lg shadow-orange-500/20"
      data-testid="demo-upgrade-cta"
      aria-label="Start your own ReStaff trial"
    >
      {/* subtle decorative glow */}
      <div className="pointer-events-none absolute -top-16 -right-12 w-64 h-64 rounded-full bg-white/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-12 w-64 h-64 rounded-full bg-amber-200/20 blur-3xl" />

      <div className="relative p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5">
        {/* Left: copy */}
        <div className="flex-1 min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur-sm px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            You're in the demo
          </span>
          <h3 className="mt-2.5 text-xl md:text-2xl font-bold tracking-tight leading-tight">
            Like what you see? Launch your own workspace.
          </h3>
          <p className="mt-1.5 text-sm md:text-[15px] text-white/90 leading-snug">
            This demo is read-only. Spin up your own ReStaff tenant in under a minute — schedule, payroll, and Eva AI included.
          </p>

          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] text-white/95">
            <li className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-200" />
              14-day free trial
            </li>
            <li className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-200" />
              No credit card required
            </li>
            <li className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-200" />
              Cancel anytime
            </li>
          </ul>
        </div>

        {/* Right: CTA button */}
        <div className="flex-shrink-0 w-full md:w-auto">
          <Link
            to="/register"
            className="group inline-flex items-center justify-center gap-2 w-full md:w-auto rounded-xl bg-white text-orange-600 font-bold py-3 px-5 md:px-6 hover:bg-slate-900 hover:text-white transition-colors shadow-md hover:shadow-xl"
            data-testid="demo-upgrade-cta-button"
          >
            <Sparkles size={16} className="text-orange-500 group-hover:text-amber-300 transition-colors" />
            Start Your Free Trial
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
